import { useEffect, useRef } from 'react';
import {
  type Application,
  type SubscriptionType,
} from '@doxyme/extensions-graphql-types';
import {
  useRegistryForPatientSingleton,
  useRegistrySingleton,
} from '@doxyme/extensions-bridge-react';
import { useLocale } from '@doxyme/locale';
import { type UserSubscriptionType } from '../../../services/subscriptions';
import {
  bridge,
  registry,
  registryWithInstall,
} from '../../../features/extensions/instances';
import { getWaitingRoom } from '../../../features/waitingRoom/waitingRoom.selectors';
import { RealtimeMessageType } from '../../../features/realtime/realtime.types';
import { subscribeToMessage } from '../../../features/realtime/realtime.subscribe';
import { getNonOfflineMemberIds } from '../../../features/realtime/realtime.selectors';
import * as realtime from '../../../features/realtime/realtime.slice';
import { useAppSelector, useAppDispatch } from '../../useReduxToolkit';
import { getRegistryCompatibleISO } from './getRegistryCompatibleISO';
import { useGetAccessToken } from '../../useCredentials';
import { usePatientPaidStatus } from '../../../hooks/usePatientPaidStatus';
import { TrackedEvents } from '../../../features/tracking/track';
import { useTrack } from '../../useTrack';

export const getRegistrySubscriptionType = (
  subscriptionType?: UserSubscriptionType,
): SubscriptionType | undefined => {
  return subscriptionType
    ? ((subscriptionType.charAt(0).toUpperCase() +
        subscriptionType.slice(1)) as SubscriptionType)
    : undefined;
};

export function useExtensionsSetupRegistry(): void {
  const registrySingleton = useRegistrySingleton();
  const { locale } = useLocale();
  const token = useGetAccessToken();
  const track = useTrack();

  const dispatch = useAppDispatch();
  const memberIds = useAppSelector(getNonOfflineMemberIds);

  const sendUpdateRef = useRef<() => Promise<void> | undefined>(
    () => undefined,
  );
  sendUpdateRef.current = async (): Promise<void> => {
    await dispatch(
      realtime.sendMessageMany({
        ids: memberIds,
        message: {
          messageType: RealtimeMessageType.ExtensionRegistryUpdate,
        },
      }),
    );
  };

  // Keep a live ref to the apps list so the install wrapper below never
  // closes over a stale snapshot from a previous render cycle.
  const appsRef = useRef<Application[]>(registrySingleton.apps);
  appsRef.current = registrySingleton.apps;

  // Keep a live ref to track so the install wrapper always uses the latest
  // session-enriched version without needing to re-register with setInstallApp.
  const trackRef = useRef(track);
  trackRef.current = track;

  useEffect(() => {
    if (registrySingleton.isInitialized) {
      registry.initApiClient(`Bearer ${token}`);

      registrySingleton.setDependencies({
        bridge,
        registry,
        onRegistryUpdate: async (): Promise<void> => sendUpdateRef.current?.(),
        locale: getRegistryCompatibleISO(locale),
      });

      void registrySingleton.refresh();
    }
  }, [registrySingleton.isInitialized, locale]);

  useEffect(() => {
    if (!registrySingleton.isInitialized) {
      return;
    }

    registryWithInstall.setInstallApp(async (extensionId: string) => {
      await registrySingleton.activate(extensionId);

      const app = appsRef.current.find((a) => a.id === extensionId);
      if (app) {
        void trackRef.current(TrackedEvents.AppInstallSuccessful, {
          appId: app.id,
          appName: app.manifest.name,
          appTitle: app.manifest.title,
          appCategory: app.category,
          source: 'windowRegistry',
        });
      }
    });

    return () => {
      registryWithInstall.clearInstallApp();
    };
  }, [registrySingleton.isInitialized, registrySingleton.activate]);
}

export function useExtensionsSetupRegistryForPatient(): void {
  const registrySingleton = useRegistryForPatientSingleton();
  const waitingRoom = useAppSelector(getWaitingRoom);
  const { roomOwnerSubscriptionType } = usePatientPaidStatus(waitingRoom);

  useEffect(() => {
    if (registrySingleton.isInitialized) {
      registrySingleton.setDependencies(
        bridge,
        registry,
        getRegistrySubscriptionType(roomOwnerSubscriptionType),
        waitingRoom.tenantId ?? undefined,
        waitingRoom.owner?.legacyUserId?.toString() || undefined,
      );

      void registrySingleton.refresh();
    }
  }, [registrySingleton.isInitialized]);

  useEffect(() => {
    const unsubscribe = subscribeToMessage({
      messageType: RealtimeMessageType.ExtensionRegistryUpdate,
      callback: () => {
        void registrySingleton.refresh();
      },
    });

    return unsubscribe;
  }, [registrySingleton.refresh]);
}
